import { TrashIcon } from '@phosphor-icons/react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { NumericInputFormField, SelectFormField } from '@/components/form'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { NavbarMenu, NavbarMenuItem } from '@/components/ui/navbar-menu'
import { Typography } from '@/components/ui/typography'
import { formatDateOnly } from '@/utils'

import * as S from '../parking-modals.styled'
import { VehicleFormData } from '../parking-modals.types'

interface ResidentSelectOption {
  value: number
  label: string
  status: string
  statusColor: 'green' | 'neutral'
  active: boolean
  moveInDate?: number | null
  moveOutDate?: number | null
}

interface VehiclesFormProps {
  activeVehicleIndex: number
  vehicleFields: VehicleFormData[]
  disabled: boolean
  disabledLicensePlate: boolean
  isResidentReadOnly?: boolean
  onTabChange: (index: number) => void
  onRemoveVehicle: () => void
  residentOptions?: ResidentSelectOption[]
}

export const VehiclesForm = ({
  activeVehicleIndex,
  vehicleFields,
  disabled,
  disabledLicensePlate,
  isResidentReadOnly = false,
  onTabChange,
  onRemoveVehicle,
  residentOptions,
}: VehiclesFormProps) => {
  const { t } = useTranslation(['parking-management'])
  const {
    formState: { errors },
    watch,
  } = useFormContext<{ vehicles: VehicleFormData[] }>()
  const vehicleErrors = errors.vehicles
  const rootVehicleError = (vehicleErrors as { root?: { message?: string } } | undefined)?.root
    ?.message

  const activeFieldId = vehicleFields[activeVehicleIndex]?.vehicleId
  const activeResidentId = watch(`vehicles.${activeVehicleIndex}.residentId`)

  const activeResident =
    residentOptions?.find((option) => option.value === activeResidentId) ??
    residentOptions?.find((option) => option.active)

  const hasMoveDates = !!(activeResident?.moveInDate || activeResident?.moveOutDate)

  return (
    <>
      <NavbarMenu>
        {vehicleFields.map((field, index) => (
          <NavbarMenuItem
            key={field.vehicleId}
            active={activeVehicleIndex === index}
            error={!!vehicleErrors?.[index]}
            onClick={() => onTabChange(index)}
          >
            {t('parking-management:vehiclesForm.carTab', { number: index + 1 })}
          </NavbarMenuItem>
        ))}
      </NavbarMenu>
      {rootVehicleError && (
        <Typography size="text-small" color="red500">
          {rootVehicleError}
        </Typography>
      )}

      <S.VehicleFormInputsWrapper>
        {residentOptions && (
          <S.ResidentSelectContainer $hasDate={hasMoveDates}>
            <SelectFormField<{ vehicles: VehicleFormData[] }, number>
              key={`${activeFieldId}-resident`}
              hideCaretOnDisable
              label={t('parking-management:assignmentType.resident')}
              options={residentOptions}
              placeholder={t('parking-management:vehiclesForm.residentPlaceholder')}
              name={`vehicles.${activeVehicleIndex}.residentId`}
              size="medium"
              disabled={disabled || isResidentReadOnly}
            />
            {hasMoveDates && (
              <S.MoveInOutDate size="text-small" color="primary500">
                {t('parking-management:vehiclesForm.moveInOutDate', {
                  moveIn: activeResident?.moveInDate
                    ? formatDateOnly(activeResident.moveInDate)
                    : '',
                  moveOut: activeResident?.moveOutDate
                    ? formatDateOnly(activeResident.moveOutDate)
                    : '',
                })}
              </S.MoveInOutDate>
            )}
          </S.ResidentSelectContainer>
        )}

        <S.LicensePlateFieldContainer>
          <TextInputFormField
            key={`${activeFieldId}-license-plate`}
            label={t('parking-management:vehiclesForm.licensePlateLabel')}
            name={`vehicles.${activeVehicleIndex}.licensePlate`}
            placeholder={t('parking-management:vehiclesForm.licensePlatePlaceholder')}
            disabled={disabledLicensePlate}
            maxLength={10}
          />
          <Button onlyBox use="secondary" disabled={disabled} onClick={onRemoveVehicle}>
            <Icon icon={TrashIcon} color="red500" />
          </Button>
        </S.LicensePlateFieldContainer>

        <S.Row>
          <TextInputFormField
            key={`${activeFieldId}-permit-tag`}
            label={t('parking-management:vehiclesForm.permitTagLabel')}
            name={`vehicles.${activeVehicleIndex}.permitTag`}
            placeholder={t('parking-management:vehiclesForm.permitTagPlaceholder')}
            disabled={disabled}
          />
          <TextInputFormField
            key={`${activeFieldId}-source`}
            disabled
            label={t('parking-management:vehiclesForm.createdByLabel')}
            name={`vehicles.${activeVehicleIndex}.source`}
          />
        </S.Row>

        <S.Row>
          <TextInputFormField
            key={`${activeFieldId}-make`}
            label={t('parking-management:vehiclesForm.makeLabel')}
            name={`vehicles.${activeVehicleIndex}.make`}
            placeholder={t('parking-management:vehiclesForm.makePlaceholder')}
            disabled={disabled}
          />
          <TextInputFormField
            key={`${activeFieldId}-model`}
            label={t('parking-management:vehiclesForm.modelLabel')}
            name={`vehicles.${activeVehicleIndex}.model`}
            placeholder={t('parking-management:vehiclesForm.modelPlaceholder')}
            disabled={disabled}
          />
        </S.Row>

        <S.Row>
          <TextInputFormField
            key={`${activeFieldId}-color`}
            label={t('parking-management:vehiclesForm.colorLabel')}
            name={`vehicles.${activeVehicleIndex}.color`}
            placeholder={t('parking-management:vehiclesForm.colorPlaceholder')}
            disabled={disabled}
          />
          <NumericInputFormField
            key={`${activeFieldId}-year`}
            label={t('parking-management:vehiclesForm.yearLabel')}
            name={`vehicles.${activeVehicleIndex}.year`}
            placeholder={t('parking-management:vehiclesForm.yearPlaceholder')}
            disabled={disabled}
          />
        </S.Row>
      </S.VehicleFormInputsWrapper>
    </>
  )
}
